import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash, Sparkle } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";

export default function Assessments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/assessments").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const remove = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Delete this assessment?")) return;
    try { await api.delete(`/assessments/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="assessments-page">
      <PageHeader
        eyebrow="Library"
        title="Origination dossiers"
        subtitle="Every research mandate your team has commissioned, with status and qualification scores."
        action={
          <Link to="/assessments/new" className="hidden sm:inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors card-elev" data-testid="assessments-new">
            <Sparkle weight="bold" size={14} /> New mandate
          </Link>
        }
      />

      {loading && <div className="mt-10 text-sm text-muted-foreground" data-testid="assessments-loading">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="mt-10 bg-card border border-border rounded-2xl p-10 sm:p-16 text-center card-elev" data-testid="assessments-empty">
          <div className="inline-flex h-12 w-12 bg-teal-soft text-teal rounded-xl items-center justify-center mb-4 mx-auto">
            <Sparkle size={20} weight="bold" />
          </div>
          <div className="font-display text-2xl mb-2 tracking-tight">No mandates yet</div>
          <div className="text-sm text-muted-foreground mb-6 max-w-md mx-auto leading-relaxed">Brief your first mandate — country, sector and capital envelope — and we&apos;ll generate an institutional-grade origination dossier.</div>
          <Link to="/assessments/new" className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-2.5 text-sm font-semibold card-elev" data-testid="assessments-empty-cta">Open new mandate</Link>
        </div>
      )}

      {!loading && items.length > 0 && (
        <>
          {/* Mobile cards */}
          <div className="mt-8 grid grid-cols-1 sm:hidden gap-3">
            {items.map((a) => (
              <Link key={a.id} to={`/assessments/${a.id}`} className="block bg-card border border-border rounded-xl p-4 active:bg-secondary transition-colors" data-testid={`asm-link-${a.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{a.title}</div>
                    <div className="text-xs text-muted-foreground mt-1 truncate">{a.countries.join(", ")}</div>
                  </div>
                  <StatusBadge status={a.status} />
                </div>
                <div className="mt-3 flex items-end justify-between">
                  <div className="text-xs">
                    <div className="text-muted-foreground">{a.sector}</div>
                    <div className="font-mono text-foreground mt-0.5">€{a.budget_eur.toLocaleString()}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="data-large text-xl text-primary">{a.scores?.opportunity_score || "—"}<span className="text-xs text-muted-foreground font-normal">/100</span></div>
                    <button onClick={(e) => remove(e, a.id)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`asm-delete-${a.id}`} aria-label="Delete"><Trash size={16} /></button>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block mt-10 bg-card border border-border rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-secondary">
                <tr>
                  <th className="text-left p-4 text-overline text-muted-foreground">Title</th>
                  <th className="text-left p-4 text-overline text-muted-foreground">Countries</th>
                  <th className="text-left p-4 text-overline text-muted-foreground">Sector</th>
                  <th className="text-left p-4 text-overline text-muted-foreground">Budget</th>
                  <th className="text-left p-4 text-overline text-muted-foreground">Status</th>
                  <th className="text-right p-4 text-overline text-muted-foreground">Score</th>
                  <th className="text-right p-4"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.id} className="border-t border-border hover:bg-secondary/50 transition-colors">
                    <td className="p-4"><Link to={`/assessments/${a.id}`} className="font-medium hover:text-primary transition-colors">{a.title}</Link></td>
                    <td className="p-4 text-sm text-muted-foreground">{a.countries.join(", ")}</td>
                    <td className="p-4 text-sm text-muted-foreground">{a.sector}</td>
                    <td className="p-4 text-sm text-muted-foreground font-mono">€{a.budget_eur.toLocaleString()}</td>
                    <td className="p-4"><StatusBadge status={a.status} /></td>
                    <td className="p-4 text-right data-large text-lg text-primary">{a.scores?.opportunity_score || "—"}</td>
                    <td className="p-4 text-right">
                      <button onClick={(e) => remove(e, a.id)} className="text-muted-foreground hover:text-destructive" aria-label="Delete"><Trash size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
